/**
 * Prices by weekday on one schedule.
 *
 * A weekday slot can carry its own `priceCents` (Monday $180, Wednesday and
 * Friday $120). A dated price change (migration 0078) then carries each of
 * those weekdays' new prices as well (migration 0094), keyed by weekday.
 *
 * Pure and shared: the Worker prices visits with it when it writes them, and
 * the browser previews a scheduled increase and summarizes the prices with
 * the same arithmetic.
 */

import { BASIS_POINTS_SCALE, MAX_AMOUNT_CENTS } from './constants/index.ts';
import { roundToCurrencyStep } from './currency.ts';
import { dayOfWeekOf, type DaySlot } from './calculations/recurrence.ts';

export interface DayPrice {
  /** 0 = Sunday … 6 = Saturday. */
  dayOfWeek: number;
  priceCents: number;
}

/**
 * Each weekday's own price, from the schedule's slots. A weekday with no
 * price of its own is absent: it follows the schedule's price.
 */
export function slotDayPrices(slots: readonly DaySlot[] | null | undefined): DayPrice[] {
  const prices = new Map<number, number>();
  for (const slot of slots ?? []) {
    if (slot.priceCents == null || prices.has(slot.dayOfWeek)) continue;
    prices.set(slot.dayOfWeek, slot.priceCents);
  }
  return [...prices.entries()]
    .sort(([left], [right]) => left - right)
    .map(([dayOfWeek, priceCents]) => ({ dayOfWeek, priceCents }));
}

/** The stored column (`{"1": 19000}`), or null when no weekday has a price. */
export function serializeScheduledDayPrices(prices: readonly DayPrice[] | null | undefined): string | null {
  if (!prices || prices.length === 0) return null;
  const sorted = [...prices].sort((left, right) => left.dayOfWeek - right.dayOfWeek);
  return JSON.stringify(Object.fromEntries(sorted.map((price) => [String(price.dayOfWeek), price.priceCents])));
}

/** Anything that is not a weekday-keyed object of whole amounts reads as "no weekday prices". */
export function parseScheduledDayPrices(json: string | null | undefined): DayPrice[] | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const prices = Object.entries(parsed as Record<string, unknown>).flatMap(([key, value]) => {
      const dayOfWeek = Number(key);
      if (!Number.isInteger(dayOfWeek) || dayOfWeek < 0 || dayOfWeek > 6) return [];
      if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) return [];
      return [{ dayOfWeek, priceCents: value }];
    });
    return prices.length > 0 ? prices.sort((left, right) => left.dayOfWeek - right.dayOfWeek) : null;
  } catch {
    return null;
  }
}

/** The pricing half of a schedule, as `occurrencePriceCents` reads it. */
export interface SchedulePricing {
  visitPriceCents: number | null;
  scheduledPriceCents: number | null;
  scheduledPriceEffectiveOn: string | null;
  scheduledDayPrices: readonly DayPrice[] | null;
}

/**
 * Whether a dated change is pending: a date, and a new schedule price or new
 * weekday prices. A schedule with no price of its own can change only its
 * weekday prices, so the schedule price in the change is then null.
 */
export function pendingChange(pricing: SchedulePricing): boolean {
  return (
    pricing.scheduledPriceEffectiveOn !== null &&
    (pricing.scheduledPriceCents !== null || (pricing.scheduledDayPrices?.length ?? 0) > 0)
  );
}

/**
 * What a visit on `date` costs, from its slot and the schedule's prices.
 *
 * Highest first: the weekday's price in a pending change (on or after its
 * date), the weekday's own price, the schedule's price in a pending change,
 * the schedule's price. The first clean is priced before this is asked.
 *
 * The weekday is the slot's when there is one — a monthly "1st Tuesday" slot
 * prices as Tuesday — and the date's otherwise.
 */
export function occurrencePriceCents(pricing: SchedulePricing, date: string, slot?: DaySlot | null): number | null {
  const changed = pendingChange(pricing) && date >= (pricing.scheduledPriceEffectiveOn as string);
  return weekdayPriceCents(pricing, slot?.dayOfWeek ?? dayOfWeekOf(date), slot?.priceCents ?? null, changed);
}

function weekdayPriceCents(
  pricing: SchedulePricing,
  weekday: number,
  slotPriceCents: number | null,
  changed: boolean,
): number | null {
  if (changed) {
    const scheduled = pricing.scheduledDayPrices?.find((price) => price.dayOfWeek === weekday);
    if (scheduled) return scheduled.priceCents;
  }
  if (slotPriceCents !== null) return slotPriceCents;
  // A change that names only weekday prices (a schedule with no price of its
  // own, raised by a contract) leaves the other days where they were.
  return changed ? (pricing.scheduledPriceCents ?? pricing.visitPriceCents) : pricing.visitPriceCents;
}

/**
 * What each weekday costs, Sunday first, either side of the pending change:
 * the same precedence as `occurrencePriceCents`, for every weekday at once.
 * Repricing visits already on the calendar works weekday by weekday from it.
 */
export function weekdayPriceTable(
  pricing: SchedulePricing,
  slots: readonly DaySlot[] | null | undefined,
  changed: boolean,
): Array<number | null> {
  const own = slotDayPrices(slots);
  return Array.from({ length: 7 }, (_, weekday) =>
    weekdayPriceCents(
      pricing,
      weekday,
      own.find((price) => price.dayOfWeek === weekday)?.priceCents ?? null,
      changed && pendingChange(pricing),
    ),
  );
}

/** How a scheduled increase reaches each weekday's own price. */
export type DayPriceIncrease = { kind: 'amount'; cents: number } | { kind: 'percent'; basisPoints: number };

/**
 * Applies one increase to a price: the same amount, or the same percentage,
 * rounded onto what the currency can charge and held to the amount ceiling.
 */
export function increasedPrice(priceCents: number, increase: DayPriceIncrease, currencyCode: string): number {
  const raw =
    increase.kind === 'amount'
      ? priceCents + increase.cents
      : (priceCents * (BASIS_POINTS_SCALE + increase.basisPoints)) / BASIS_POINTS_SCALE;
  return Math.min(MAX_AMOUNT_CENTS, Math.max(0, roundToCurrencyStep(raw, currencyCode)));
}

/** Each weekday's price after the same increase. Empty when no weekday has its own price. */
export function increasedDayPrices(
  slots: readonly DaySlot[] | null | undefined,
  increase: DayPriceIncrease,
  currencyCode: string,
): DayPrice[] {
  return slotDayPrices(slots).map((price) => ({
    dayOfWeek: price.dayOfWeek,
    priceCents: increasedPrice(price.priceCents, increase, currencyCode),
  }));
}

/**
 * The schedule's prices grouped by amount, in weekday order of first
 * appearance: Mon $180 · Wed, Fri $120. Weekdays without a price of their own
 * carry the schedule's. Null when the schedule has no weekday prices, so the
 * caller shows its usual single price.
 */
export function dayPriceGroups(
  slots: readonly DaySlot[] | null | undefined,
  visitPriceCents: number | null,
): Array<{ days: number[]; priceCents: number | null }> | null {
  const own = slotDayPrices(slots);
  if (own.length === 0) return null;
  const weekdays = [...new Set((slots ?? []).map((slot) => slot.dayOfWeek))];
  // Monday first, Sunday last: the way a cleaning week is read.
  weekdays.sort((left, right) => ((left + 6) % 7) - ((right + 6) % 7));
  const groups: Array<{ days: number[]; priceCents: number | null }> = [];
  for (const day of weekdays) {
    const priceCents = own.find((price) => price.dayOfWeek === day)?.priceCents ?? visitPriceCents;
    const group = groups.find((candidate) => candidate.priceCents === priceCents);
    if (group) group.days.push(day);
    else groups.push({ days: [day], priceCents });
  }
  return groups;
}

/** "Mon" / "lun", from the reader's locale. 2026-01-04 is a Sunday. */
export function weekdayShortName(dayOfWeek: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(
    new Date(Date.UTC(2026, 0, 4 + dayOfWeek, 12)),
  );
}

/**
 * "Mon $180 · Wed, Fri $120" — a schedule's prices by weekday, for a header
 * or a notice. Null when no weekday has its own price, so the caller shows
 * the single price it always has. `money` formats an amount, and is handed
 * null for a weekday that is not billed.
 */
export function dayPriceSummary(
  slots: readonly DaySlot[] | null | undefined,
  visitPriceCents: number | null,
  money: (cents: number | null) => string,
  locale: string,
): string | null {
  const groups = dayPriceGroups(slots, visitPriceCents);
  if (!groups) return null;
  return groups
    .map((group) => `${group.days.map((day) => weekdayShortName(day, locale)).join(', ')} ${money(group.priceCents)}`)
    .join(' · ');
}

/** The slots as they will be once these weekday prices apply. */
export function withDayPrices(
  slots: readonly DaySlot[] | null | undefined,
  prices: readonly DayPrice[] | null | undefined,
): DaySlot[] | null {
  if (!slots) return null;
  return slots.map((slot) => {
    const next = prices?.find((price) => price.dayOfWeek === slot.dayOfWeek);
    return next ? { ...slot, priceCents: next.priceCents } : slot;
  });
}
